import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const TermsOfService = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-8 text-center">Terms of Service</h1>
          <p className="text-muted-foreground mb-8 text-center">Last updated: September 12, 2025</p>
          
          <div className="prose prose-lg max-w-none space-y-8">
            <p className="text-lg">
              These Terms govern your use of https://www.frostedcakeart.com (the "Site") and any orders placed with Frosted Cake Art LLC ("we," "us," "our"). By using the Site or placing an order, you agree to these Terms.
            </p>

            <section>
              <h2 className="text-2xl font-semibold mb-4">1) Acceptance of Terms & Website Use</h2>
              <p className="mb-4">
                All Site content (text, photos, logos, designs) is owned by us or used with permission. Do not copy, distribute, or modify without our written consent.
              </p>
              <p>
                You agree not to misuse the Site (including attempting to breach security, scraping, or interfering with normal operation).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2) Inquiries, Quotes & Pricing</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Quotes are estimates based on the details you provide (design, servings, flavors, delivery distance, timeline).</li>
                <li>Unless stated otherwise, quotes expire 14 days from the date issued.</li>
                <li>Final price may change if order details change, if specialty materials are required, or due to availability constraints.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3) Order Documents & Precedence</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>For specific projects, we may issue an invoice, estimate, proposal, order confirmation, email confirmation, or statement of work (each an "Order Document").</li>
                <li>If an Order Document includes terms/conditions, those project-specific terms govern that project and take precedence over these Terms to the extent of any conflict.</li>
                <li>If an Order Document does not include terms/conditions, these Terms apply by default.</li>
                <li>Any change to an Order Document or to these Terms must be in writing and accepted by Frosted Cake Art LLC.</li>
                <li>Customer-supplied terms (e.g., on a PO) do not apply unless we expressly agree in writing.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4) Deposits, Payments & Due Dates</h2>
              <p>A non-refundable deposit of 50% is required to reserve your date.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5) Changes, Cancellations & Refunds</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Design/flavor changes must be requested at least 14 days before the event; late changes may not be possible and/or may incur additional charges.</li>
                <li>Cancellation: Deposits are non-refundable. If canceled within 14 days of the event, up to the full balance may be due to cover materials, prep, and reserved time.</li>
                <li>Any refunds/credits are at our discretion and limited to amounts paid for the affected items.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6) Allergies, Food Safety & Care</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Our products may contain or come into contact with egg, dairy, wheat (gluten), nuts, soy and other allergens.</li>
                <li>You must notify us in writing at the time of ordering of any allergies or dietary restrictions. We cannot guarantee an allergen-free environment.</li>
                <li>Follow our storage and serving instructions. We are not responsible for issues arising from improper handling, storage, or exposure to heat/sun.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7) Delivery, Pickup & Risk of Loss</h2>
              <p className="mb-4"><strong>Service area:</strong> Greater Cleveland & Northeast Ohio.</p>
              
              <h3 className="text-xl font-medium mb-3">Pickup:</h3>
              <p className="mb-4">Inspect items at pickup; acceptance confirms good condition. Risk of loss/damage transfers to you at pickup.</p>

              <h3 className="text-xl font-medium mb-3">Delivery:</h3>
              <p className="mb-4">We deliver at the scheduled time; minor delays may occur due to traffic/weather. If no one is available to receive the order, we may leave it in a safe location or return it (additional fees may apply). Risk transfers upon delivery.</p>

              <h3 className="text-xl font-medium mb-3">Display conditions:</h3>
              <p>Provide a stable, level table and a cool environment (≤ 72°F) for display to avoid melting or structural issues.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8) Artistic Interpretation & Materials</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Custom cakes are handcrafted; slight variations in color, texture, and decoration are normal and part of the artistic process.</li>
                <li>Certain elements (e.g., fresh flowers, support dowels, wires, non-edible toppers) may be non-edible; these will be disclosed and must be removed before serving.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9) Photos, Testimonials & Portfolio Use</h2>
              <p>
                We may photograph your order and use images/testimonials for our portfolio, Site, and social media. To opt out, email fcabakery@gmail.com before your event date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10) Event Changes & Force Majeure</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>We are not liable for delays or non-performance caused by events beyond our control (e.g., extreme weather, accidents, illness, supply shortages, power outages, strikes).</li>
                <li>If such an event occurs, we will act in good faith to offer a reasonable alternative, reschedule, or credit where appropriate.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11) Warranties & Limitation of Liability</h2>
              <p className="mb-4">
                Products are provided "as is" without warranties. Our liability is limited to the amount paid for the specific item(s) in question.
              </p>
              <p>
                We are not liable for indirect, incidental, special, or consequential damages arising from your use of our products or services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12) Governing Law & Disputes</h2>
              <p>
                These Terms are governed by Ohio law. Any disputes will be resolved through binding arbitration or in the courts of Cuyahoga County, Ohio.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Contact</h2>
              <div className="space-y-2">
                <p><strong>Frosted Cake Art LLC</strong></p>
                <p>Email: fcabakery@gmail.com • Phone: 216-571-8440</p>
                <p>Mail: 504 Manor Brook Dr, Chagrin Falls, OH 44022</p>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;